import { buildQueryKey } from '@webapp/core/utils/react-query/buildQueryKey'
import { orderItemRepository } from '../data-access/repositories/OrderItemRepository'
import { type ApprovedOrderItemListParams } from '../data-access/types/ApprovedOrderItemListParams'
import { type OrderItemRecurrenceDurationEnum } from '../entities/enums/OrderItemRecurrenceDurationEnum'
import { type OrderItemRecurrenceFrequencyEnum } from '../entities/enums/OrderItemRecurrenceFrequencyEnum'

const base = ['order-item']

// Prefix of every listApprovedItems(params) key — account-code-correction
// Models invalidate all param variants through this. The sibling
// 'approved-count' badge key sits outside it by design.
export const ORDER_ITEM_APPROVED_BASE_KEY = [...base, 'approved']

export const orderItemQueryConfig = {
  getPurchasingListItems: () => ({
    queryKey: buildQueryKey([...base, 'purchasing-list']),
    queryFn: () => orderItemRepository.getPurchasingListItems(),
  }),

  // Plain array (not buildQueryKey): productUrl can be undefined and must
  // remain part of the key rather than being truncated.
  extractProductData: (productUrl?: string) => ({
    queryKey: [...base, 'extract-data', productUrl],
  }),

  // Plain array (not buildQueryKey): params can be undefined and must remain
  // part of the key rather than being truncated.
  getRecurrencePeriodCount: (params?: {
    duration: OrderItemRecurrenceDurationEnum
    durationQuantity: number
    frequency: OrderItemRecurrenceFrequencyEnum
    startDate: string
  }) => ({
    queryKey: [...base, 'recurrence', 'period-count', params],
  }),

  getAccountCodeSuggestions: ({
    userId,
    secondaryCategoryId,
  }: {
    userId: number
    secondaryCategoryId: number
  }) => ({
    queryKey: buildQueryKey([
      ...base,
      'account-code-suggestions',
      secondaryCategoryId,
    ]),
    queryFn: () =>
      orderItemRepository.getAccountCodeSuggestions(
        userId,
        secondaryCategoryId
      ),
  }),

  getPreferredVendorSuggestions: ({
    userId,
    legalEntityId,
  }: {
    userId: number
    legalEntityId?: Nullable<number>
  }) => ({
    queryKey: buildQueryKey([
      ...base,
      'preferred-vendor-suggestions',
      legalEntityId,
    ]),
    queryFn: () =>
      orderItemRepository.getPreferredVendorSuggestions(userId, legalEntityId),
  }),

  listApprovedItems: (params?: ApprovedOrderItemListParams) => ({
    queryKey: buildQueryKey([...ORDER_ITEM_APPROVED_BASE_KEY, params]),
    queryFn: () => orderItemRepository.listApprovedItems(params),
  }),
  // Sibling segment outside the ['order-item', 'approved'] list prefix by
  // design — corrections never refetch the badge
  approvedItemCount: () => ({
    queryKey: buildQueryKey([...base, 'approved-count']),
    queryFn: () => orderItemRepository.getApprovedItemCount(),
  }),
}
